import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Sparkle } from "@phosphor-icons/react";

const NAV = [
  { label: "Home Dashboard", to: "/" },
  { label: "Sales Invoice — new", to: "/invoices/sales/new" },
  { label: "Purchase Bill — new", to: "/invoices/purchase/new" },
  { label: "Vouchers / Day Book", to: "/vouchers" },
  { label: "Chart of Accounts", to: "/masters/ledgers" },
  { label: "Parties (Customers & Vendors)", to: "/masters/parties" },
  { label: "Items & HSN", to: "/masters/items" },
  { label: "Inventory & Stock Ledger", to: "/inventory" },
  { label: "Banking & Reconciliation", to: "/banking" },
  { label: "Reports", to: "/reports" },
  { label: "GST Returns Studio", to: "/compliance" },
  { label: "Migration Wizard (Tally / Excel)", to: "/migration" },
  { label: "AI Copilot", to: "/copilot" },
  { label: "Settings & Team", to: "/settings" },
];

export default function CommandPalette({ open, setOpen, onDraft }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [setOpen]);

  const draft = async () => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/draft-voucher", { text: value });
      onDraft?.(data);
      setOpen(false);
      setValue("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl overflow-hidden border-border bg-popover/95 p-0 backdrop-blur-xl" data-testid="command-palette">
        <Command shouldFilter className="bg-transparent">
          <CommandInput
            data-testid="command-palette-input"
            placeholder='Jump to a screen, or type a transaction: "sales bill Ram 12500 GST 18"'
            value={value}
            onValueChange={setValue}
          />
          <CommandList className="max-h-[420px]">
            {value.trim().length > 3 && (
              <CommandGroup heading="AI Action">
                <CommandItem
                  value={`ai-draft-${value}`}
                  onSelect={draft}
                  data-testid="command-palette-ai-draft"
                  className="gap-2"
                >
                  <Sparkle size={16} className="text-primary" />
                  <span className="truncate">
                    {busy ? "Drafting voucher…" : `Draft voucher from: “${value}”`}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandEmpty>No matches.</CommandEmpty>
            <CommandGroup heading="Navigate">
              {NAV.map((n) => (
                <CommandItem
                  key={n.to}
                  value={n.label}
                  onSelect={() => {
                    navigate(n.to);
                    setOpen(false);
                    setValue("");
                  }}
                  data-testid={`command-nav-${n.to.replace(/\//g, "-")}`}
                >
                  {n.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
